// frontend/src/App.tsx
import { useEffect, useState, Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary } from './errors/ErrorBoundary'
import { DevToolsPanel } from './dev/DevToolsPanel'
import Onboarding from './pages/Onboarding'
import RequireRole from './components/RequireRole'
import { fetchIdentity, type Identity } from './lib/auth'
import AdminRouter from '@/app/admin/AdminRouter'

// ---------- Lazy pages (real pages, no placeholders) ----------
const ChatPage          = lazy(() => import('./pages/Chat'))
const AdminModels       = lazy(() => import('./pages/admin/Models'))
const AdminPricing      = lazy(() => import('./pages/admin/Pricing'))
const AdminPolicies     = lazy(() => import('./pages/admin/Policies'))
const ObservabilityPage = lazy(() => import('./pages/admin/Observability'))
const MemoryPage        = lazy(() => import('./pages/admin/Memory'))

// Workflow Composer (adapter onto React Flow UI)
const WorkflowComposerPage = lazy(
  () => import('./features/workflow/WorkflowComposerAdapter')
)

// Dev-only: Feature Flags panel; no-op in prod
const FeatureFlagSwitchboard = import.meta.env.DEV
  ? lazy(() => import('./dev/FeatureFlagSwitchboard'))
  : lazy(async () => ({ default: () => null }))

const qc = new QueryClient()

/** Small wrapper to avoid 403 flicker while identity is still loading */
function AdminRoute(props: { identity: Identity | null; children: JSX.Element }) {
  const { identity, children } = props
  if (!identity) {
    return <div style={{ padding: 16, fontSize: 14, color: '#555' }}>Loading identity…</div>
  }
  return <RequireRole role="admin" identity={identity}>{children}</RequireRole>
}

export default function App() {
  const [identity, setIdentity] = useState<Identity | null>(null)
  const [idErr, setIdErr] = useState<string | null>(null)

  // Load identity once; in dev, fetchIdentity guarantees roles include 'admin'
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const me = await fetchIdentity()
        if (alive) setIdentity(me)
      } catch (e: any) {
        if (alive) {
          setIdentity({ user: null, tenant: 'public', roles: [] })
          setIdErr(e?.message ?? null)
        }
      }
    })()
    return () => { alive = false }
  }, [])

  return (
    <QueryClientProvider client={qc}>
      <ErrorBoundary>
        {/* Optional visibility into identity failures without breaking pages */}
        {idErr && (
          <div style={{ padding: 12, background: '#FFF4E5', color: '#7A4D00', fontSize: 12 }}>
            Identity load warning: {idErr}
          </div>
        )}

        <Suspense fallback={<div style={{ padding: 16 }}>Loading…</div>}>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Onboarding />} />
            <Route path="/app/chat" element={<ChatPage />} />

            {/* Admin-only areas (no flicker; waits for identity) */}

            {/* NEW: mount AdminRouter for /admin/* (e.g. /admin/workflow) */}
            <Route
              path="/admin/*"
              element={
                <AdminRoute identity={identity}>
                  <AdminRouter />
                </AdminRoute>
              }
            />

            {/* Legacy Workflow Composer under /app/admin/workflows */}
            <Route
              path="/app/admin/workflows"
              element={
                <AdminRoute identity={identity}>
                  <WorkflowComposerPage />
                </AdminRoute>
              }
            />

            <Route
              path="/app/admin/models"
              element={<AdminRoute identity={identity}><AdminModels /></AdminRoute>}
            />
            <Route
              path="/app/admin/pricing"
              element={<AdminRoute identity={identity}><AdminPricing /></AdminRoute>}
            />
            <Route
              path="/app/admin/policies"
              element={<AdminRoute identity={identity}><AdminPolicies /></AdminRoute>}
            />
            <Route
              path="/app/admin/observability"
              element={<AdminRoute identity={identity}><ObservabilityPage /></AdminRoute>}
            />
            <Route
              path="/app/admin/memory"
              element={<AdminRoute identity={identity}><MemoryPage /></AdminRoute>}
            />
            <Route
              path="/app/admin/feature-flags"
              element={<AdminRoute identity={identity}><FeatureFlagSwitchboard /></AdminRoute>}
            />

            {/* Convenience redirects / 404 */}
            {/* /app/admin -> /app/admin/workflows */}
            <Route
              path="/app/admin"
              element={<Navigate to="/app/admin/workflows" replace />}
            />

            <Route path="/app" element={<Navigate to="/" replace />} />
            <Route path="*" element={<div style={{ padding: 16 }}>404: Page not found</div>} />
          </Routes>

          {/* Optional dev overlay */}
          <DevToolsPanel />
        </Suspense>
      </ErrorBoundary>
    </QueryClientProvider>
  )
}